import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase.js";
import { removeFile } from "../lib/storage.js";

/**
 * Signed rental agreements. Unlike the DL photo and COI — one file each, stored
 * on the customer row — a customer accumulates agreements over time, so they
 * live in their own table.
 *
 * Writes happen immediately rather than on form save: a customer can have many,
 * and staging a list of adds and removes to reconcile later would be a lot of
 * machinery for a list you only ever append to or prune.
 */

export function useAgreements(customerId) {
  return useQuery({
    queryKey: ["customer_agreements", customerId],
    enabled: !!customerId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customer_agreements")
        .select("id,file_path,file_name,uploaded")
        .eq("customer_id", customerId)
        .order("uploaded", { ascending: false });
      if (error) throw new Error(error.message);
      return data;
    },
  });
}

export function useAddAgreement(customerId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ path, name }) => {
      const { data, error } = await supabase
        .from("customer_agreements")
        .insert([{ customer_id: customerId, file_path: path, file_name: name }])
        .select("id");
      if (error) {
        // The file is already uploaded; without the row it would be orphaned.
        await removeFile(path);
        throw new Error(error.message);
      }
      return data[0];
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customer_agreements", customerId] });
      // The customers list shows a paperclip when agreements exist, so its
      // cached rows are stale too.
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });
}

export function useDeleteAgreement(customerId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (agreement) => {
      const { data, error } = await supabase
        .from("customer_agreements")
        .delete()
        .eq("id", agreement.id)
        .select("id");
      if (error) throw new Error(error.message);
      if (!data?.length) throw new Error("Nothing was deleted — a security policy may have blocked it.");
      await removeFile(agreement.file_path);
      return agreement.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customer_agreements", customerId] });
      // The customers list shows a paperclip when agreements exist, so its
      // cached rows are stale too.
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });
}
